import * as React from 'react';
import { observer } from 'mobx-react';
import { IResultsTableParams } from './IResultsTableParams';
import { SwitchButton } from './../../SwitchButton/SwitchButton';
import { ISwitchButtonProps } from './../../SwitchButton/ISwitchButtonProps';
import { TagsList } from './../TagsList/TagsList';
import { ITagsListParams } from './../TagsList/ITagsListParams';
import { UnitButton } from './../UnitButton/UnitButton';
import { ChangeDataPopUp } from './../ChangeDataPopUp/ChangeDataPopUp';
import { IUnitButtonParams } from './../UnitButton/IUnitButtonParams';
import { UnitSystem, TargetsHelper } from 'MeasurementsCalculations';
import { IReportMeasurementGroup, IReportMeasurement, ReportKind } from 'Data';
import { ParameterHelper } from 'MeasurementsCalculations';

export interface IResultsTableState {
  showChangeData: boolean;
}

@observer
export class ResultsTable extends React.Component<IResultsTableParams, IResultsTableState> {
  private selectedTileAdditionalInformation: string = '';

  constructor(props: IResultsTableParams) {
    super(props, undefined);

    this.state = { ...this.state, ...{ showChangeData: false } };
    this.changeDataOptions = this.changeDataOptions.bind(this);

    this.loadData(props);
  }

  componentWillReceiveProps(nextProps: IResultsTableParams) {
    this.loadData(nextProps);
  }

  loadData(props: IResultsTableParams) {
    if (props.measurementParameters.length === 0) {
      props.loadTiles();
    } else {
      props.updateUnitOnTiles();
    }
  }

  changeDataOptions() {
    this.setState({ showChangeData: !this.state.showChangeData });
  }

  collapseStrokesGroup() {
    this.props.collapseStrokesGroup(this.props.measurementGroup);
  }

  calculatePercentageColorOpacity(color: string, opacity: number): string {
    //It was implemented because in TPS when printing the opacity in svg images causes problems like scaling and translating the parts where opacity is used.
    let hex = color.replace('#', '');
    opacity = opacity / 100;

    //Color that needs to have opacity applied
    let r1 = parseInt(hex.substring(0, 2), 16);
    let g1 = parseInt(hex.substring(2, 4), 16);
    let b1 = parseInt(hex.substring(4, 6), 16);

    //White color
    let r2 = 255;
    let g2 = 255;
    let b2 = 255;

    //Color with opacity blended with white to get a solid color that looks like the color with opacity on white background

    let r3 = Math.round(r2 + (r1 - r2) * opacity);
    let g3 = Math.round(g2 + (g1 - g2) * opacity);
    let b3 = Math.round(b2 + (b1 - b2) * opacity);

    return 'rgb(' + r3 + ',' + g3 + ',' + b3 + ')';
  }

  selectShot(stroke: IReportMeasurement, group: IReportMeasurementGroup) {
    this.props.selectShot(stroke, group);
  }

  hideSingleShot(stroke: IReportMeasurement, group: IReportMeasurementGroup) {
    if (this.props.hideSingleShot) this.props.hideSingleShot(stroke, group);
  }

  selectVideo(stroke: IReportMeasurement, group: IReportMeasurementGroup) {
    if (this.props.selectVideo) this.props.selectVideo(stroke, group);
  }

  selectAverage(strokes: Array<IReportMeasurement>, measurementGroup: IReportMeasurementGroup) {
    this.props.selectAverage(strokes, measurementGroup);
  }

  render() {
    let columnHeaderStyle = {
      borderColor: this.props.measurementGroup.color,
      borderBottomColor: !this.props.measurementGroup.isSelected
        ? this.props.measurementGroup.color
        : '#c1c1c1',
      borderBottomStyle: 'solid' as 'solid',
    };
    let collapseButtonText = this.props.measurementGroup.isCollapsed ? 'Details' : 'Hide';
    let borderColorOfGroup = {
      borderColor: this.props.measurementGroup.color,
    };
    let emptyParameterCell = {
      borderColor: this.props.measurementGroup.color,
    };
    let isMultiOrSingleGroupReport =
      this.props.reportKind === ReportKind.multiGroupReport ||
      this.props.reportKind === ReportKind.singleGroupReport ||
      this.props.reportKind === ReportKind.puttingSingleGroupReport ||
      this.props.reportKind === ReportKind.puttingMultiGroupReport;
    let isDrShotAnalysisReport = this.props.reportKind === ReportKind.drShotAnalysis;

    let isCombineOrTestCenterReport =
      this.props.reportKind === ReportKind.combineTestReport ||
      this.props.reportKind === ReportKind.testCenterReport;

    let targetUnit =
      this.props.unitPresentation && this.props.unitPresentation.unitSystem === UnitSystem.Us
        ? 'yds'
        : 'm';
    let targetInUnit = TargetsHelper.targetInUnits(
      this.props.measurementGroup,
      this.props.selectedUnit
    );
    return (
      <div className="ResultsTable">
        <table className="table table-with-shots">
          <colgroup>
            <col className="column small"></col>
            <col className="column small"></col>
            <col className="column small"></col>
            <col className="column small"></col>
            <col className="column"></col>
            <col className="column"></col>
            <col className="column"></col>
            <col className="column"></col>
            <col className="column"></col>
            <col className="column"></col>
            <col className="column"></col>
            <col className="column"></col>
            <col className="column"></col>
            <col className="column"></col>
          </colgroup>
          <thead></thead>
          <tbody>
            {(isMultiOrSingleGroupReport || isDrShotAnalysisReport) && (
              <tr>
                <td colSpan={14} className="group-date-row">
                  {this.props.measurementGroup.date && (
                    <span className="group-date">{this.props.measurementGroup.date}</span>
                  )}
                </td>
              </tr>
            )}
            {isCombineOrTestCenterReport && (
              <tr className="group-separation-row">
                <td colSpan={14}></td>
              </tr>
            )}
            <tr>
              <td colSpan={14} className="stroke-group-header" style={columnHeaderStyle}>
                {(isMultiOrSingleGroupReport || isDrShotAnalysisReport) && (
                  <span>
                    <SwitchButton {...this.getSwitchButtonParams()}></SwitchButton>
                    <TagsList {...this.getTagsListParams()}></TagsList>
                    {this.props.measurementGroup.isSelected && !this.state.showChangeData && (
                      <span
                        className="collapse-details-btn"
                        onClick={() => this.collapseStrokesGroup()}
                      >
                        {collapseButtonText}
                      </span>
                    )}
                  </span>
                )}
                {isCombineOrTestCenterReport && (
                  <span
                    className="target-and-score-wrapper"
                    style={{ color: this.props.measurementGroup.color }}
                  >
                    <span className="target-value-and-unit-wrapper">
                      <span className="target-text">Target</span>
                      <span className="target-value-and-unit">
                        <span className="target-value">{targetInUnit}</span>
                        {targetInUnit !== 'Drive' &&
                          targetInUnit !== 'Junior L1' &&
                          targetInUnit !== 'Junior L2' &&
                          targetInUnit !== 'Junior L3' &&
                          this.props.unitPresentation && (
                            <span className="target-units">{targetUnit}</span>
                          )}
                      </span>
                    </span>
                    <span className="score-wrapper">
                      <span className="score-text">Score</span>
                      <span className="score-value">
                        {this.props.measurementGroup.averageScore != null
                          ? this.props.measurementGroup.averageScore.toFixed(1)
                          : this.props.measurementGroup.averageScore}
                      </span>
                    </span>
                  </span>
                )}
              </td>
            </tr>
            {this.props.measurementGroup.isSelected && this.state.showChangeData && (
              <ChangeDataPopUp
                measurementParameters={this.props.measurementParameters}
                color={this.props.measurementGroup.color ?? ''}
                changeDataOptions={this.changeDataOptions}
                disableTile={this.props.disableTile}
                reportStore={this.props.reportStore}
              ></ChangeDataPopUp>
            )}
            {this.props.measurementGroup.isSelected && (
              <tr className="parameter-names-row">
                {isDrShotAnalysisReport && <td colSpan={4} style={borderColorOfGroup}></td>}
                {!isDrShotAnalysisReport && (
                  <td
                    colSpan={4}
                    className="change-data"
                    onClick={this.changeDataOptions}
                    style={borderColorOfGroup}
                  >
                    <span data-toggle="modal">Change Data</span>
                    <img
                      className="change-data-icon"
                      src="Images/MultiShotControl/change-data-icon.svg"
                      alt="change data icon"
                    />
                  </td>
                )}
                {this.props.measurementParameters
                  .filter(x => x.isVisible)
                  .map((parameter, i) => {
                    return (
                      <td
                        data-toggle="modal"
                        key={parameter.propertyName}
                        onClick={() => this.props.setAdditionalInfoUrl(parameter.myTrackManPage)}
                        data-target={
                          parameter.myTrackManPage && this.props.isWeb
                            ? '#showAdditionalInfoOnTile'
                            : ''
                        }
                        style={{
                          borderColor: this.props.measurementGroup.color,
                          cursor:
                            parameter.myTrackManPage && this.props.isWeb ? 'pointer' : 'default',
                        }}
                      >
                        <span className="parameter-tile">
                          <span className="parameter-name">{parameter.title}</span>
                        </span>
                      </td>
                    );
                  })}
                {this.props.measurementParameters.map((parameter, i) => {
                  if (
                    i <
                    10 -
                      this.props.measurementParameters.filter(x => {
                        return x.isVisible;
                      }).length
                  ) {
                    return <td key={parameter.propertyName} style={emptyParameterCell}></td>;
                  }
                  return null;
                })}
              </tr>
            )}
            {this.props.measurementGroup.isSelected && (
              <tr className="parameter-units-row">
                <td colSpan={4} className="units-data" style={borderColorOfGroup}>
                  {(isMultiOrSingleGroupReport || isDrShotAnalysisReport) && (
                    <UnitButton {...this.getUnitButtonParams()}></UnitButton>
                  )}
                </td>
                {this.props.measurementParameters
                  .filter(x => x.isVisible)
                  .map((parameter, i) => {
                    return (
                      <td style={borderColorOfGroup} key={parameter.propertyName}>
                        <span className="parameter-tile">
                          <span className="parameter-unit">{parameter.displayUnit}</span>
                        </span>
                      </td>
                    );
                  })}
                {this.props.measurementParameters.map((parameter, i) => {
                  if (
                    i <
                    10 -
                      this.props.measurementParameters.filter(x => {
                        return x.isVisible;
                      }).length
                  ) {
                    return <td key={parameter.propertyName} style={emptyParameterCell}></td>;
                  }
                  return null;
                })}
              </tr>
            )}
            {this.props.measurementGroup.strokes &&
              this.props.measurementGroup.strokes.map((measurement, i) => {
                if (
                  this.props.measurementGroup.isCollapsed === false &&
                  this.props.measurementGroup.isSelected
                ) {
                  return (
                    <tr
                      className={`row-with-shot-details ${
                        measurement.isVisible === false ? 'deselected-shot' : ''
                      }`}
                      key={measurement.id}
                      style={{
                        borderStyle:
                          this.props.selectedStroke != null &&
                          this.props.selectedStroke.id === measurement.id
                            ? 'double'
                            : 'solid',
                        borderTopColor:
                          this.props.selectedStroke != null &&
                          this.props.selectedStroke.id === measurement.id
                            ? this.props.measurementGroup.color
                            : '#c1c1c1',
                        borderBottomColor:
                          this.props.selectedStroke != null &&
                          this.props.selectedStroke.id === measurement.id
                            ? this.props.measurementGroup.color
                            : '#c1c1c1',
                        borderLeftColor: this.props.measurementGroup.color,
                        borderRightColor: this.props.measurementGroup.color,
                        backgroundColor:
                          this.props.selectedStroke != null &&
                          this.props.selectedStroke.id === measurement.id
                            ? this.calculatePercentageColorOpacity(
                                this.props.measurementGroup.color ?? '',
                                10
                              )
                            : '#fff',
                      }}
                    >
                      <td
                        className="order-numbers"
                        onClick={
                          measurement.isVisible
                            ? () => this.selectShot(measurement, this.props.measurementGroup)
                            : () => null
                        }
                      >
                        {i + 1 + '.'}
                      </td>
                      {(isMultiOrSingleGroupReport || isDrShotAnalysisReport) && (
                        <td
                          className="visibility-btn"
                          onClick={() =>
                            this.hideSingleShot(measurement, this.props.measurementGroup)
                          }
                        >
                          {measurement.isVisible && (
                            <img src="Images/MultiShotControl/eye-show.svg" alt="Eye icon" />
                          )}
                          {measurement.isVisible === false && (
                            <img src="Images/MultiShotControl/eye-hide.svg" alt="Eye icon" />
                          )}
                        </td>
                      )}
                      {isCombineOrTestCenterReport && (
                        <td
                          className="visibility-btn"
                          onClick={() => this.selectShot(measurement, this.props.measurementGroup)}
                        ></td>
                      )}
                      {/*If video button exists*/}
                      {isMultiOrSingleGroupReport && measurement.hasVideos && (
                        <td
                          className="video-btn has-video"
                          onClick={
                            measurement.isVisible
                              ? () => {
                                  this.selectVideo(measurement, this.props.measurementGroup);
                                }
                              : () => null
                          }
                        >
                          <img src="Images/MultiShotControl/play-video.svg" alt="Play icon" />
                        </td>
                      )}
                      {((isMultiOrSingleGroupReport && measurement.hasVideos === false) ||
                        isDrShotAnalysisReport) && (
                        <td
                          className="video-btn has-video"
                          onClick={() => this.selectShot(measurement, this.props.measurementGroup)}
                        ></td>
                      )}
                      {isCombineOrTestCenterReport && (
                        <td
                          className="video-btn"
                          onClick={() => this.selectShot(measurement, this.props.measurementGroup)}
                        ></td>
                      )}
                      {(isMultiOrSingleGroupReport || isDrShotAnalysisReport) && (
                        <td>
                          <span
                            className="ball-icon"
                            onClick={
                              measurement.isVisible
                                ? () => this.selectShot(measurement, this.props.measurementGroup)
                                : () => null
                            }
                            style={{
                              backgroundColor:
                                measurement.isVisible &&
                                this.props.selectedStroke != null &&
                                this.props.selectedStroke.id === measurement.id &&
                                !this.props.selectedAverage
                                  ? this.props.measurementGroup.color
                                  : 'transparent',
                            }}
                          >
                            <img
                              src="Images/MultiShotControl/single-shot-icon-without-border.png"
                              alt="Ball icon"
                            />
                          </span>
                        </td>
                      )}
                      {isCombineOrTestCenterReport && (
                        <td
                          onClick={() => this.selectShot(measurement, this.props.measurementGroup)}
                        ></td>
                      )}
                      {this.props.measurementParameters
                        .filter(x => x.isVisible)
                        .map((parameter, i) => {
                          return (
                            <td
                              className={
                                isCombineOrTestCenterReport && parameter.propertyName === 'Score'
                                  ? 'score-values'
                                  : ''
                              }
                              key={parameter.propertyName}
                              onClick={
                                measurement.isVisible
                                  ? () => {
                                      this.selectShot(measurement, this.props.measurementGroup);
                                    }
                                  : () => null
                              }
                            >
                              {ParameterHelper.getParameterValue(
                                measurement,
                                parameter.propertyName,
                                this.props.unitPresentation
                              )}
                            </td>
                          );
                        })}
                      {this.props.measurementParameters.map((parameter, i) => {
                        if (
                          i <
                          10 - this.props.measurementParameters.filter(x => x.isVisible).length
                        ) {
                          return (
                            <td
                              key={parameter.propertyName}
                              onClick={
                                measurement.isVisible
                                  ? () => {
                                      this.selectShot(measurement, this.props.measurementGroup);
                                    }
                                  : () => null
                              }
                            ></td>
                          );
                        }
                        return null;
                      })}
                    </tr>
                  );
                }
                return null;
              })}
            {this.props.measurementGroup.isSelected && (
              <tr
                className="average-values chosen-average"
                style={{
                  backgroundColor:
                    (this.props.selectedAverage &&
                      this.props.selectedGroupOfStrokes &&
                      this.props.selectedGroupOfStrokes.id === this.props.measurementGroup.id) ||
                    (this.props.selectedAverage == null && this.props.selectedStroke == null)
                      ? this.calculatePercentageColorOpacity(
                          this.props.measurementGroup.color ?? '',
                          10
                        )
                      : '#fff',
                  borderColor: this.props.measurementGroup.color,
                }}
              >
                <td
                  colSpan={3}
                  onClick={() =>
                    this.selectAverage(
                      (this.props.measurementGroup && this.props.measurementGroup.strokes) ?? [],
                      this.props.measurementGroup
                    )
                  }
                >
                  Average
                </td>
                {(isMultiOrSingleGroupReport || isDrShotAnalysisReport) && (
                  <td>
                    <span
                      className="balls-icon"
                      onClick={() =>
                        this.selectAverage(
                          (this.props.measurementGroup && this.props.measurementGroup.strokes) ??
                            [],
                          this.props.measurementGroup
                        )
                      }
                    >
                      <img src="Images/MultiShotControl/average-icon.svg" alt="Balls icon" />
                    </span>
                  </td>
                )}
                {isCombineOrTestCenterReport && (
                  <td
                    onClick={() =>
                      this.selectAverage(
                        (this.props.measurementGroup && this.props.measurementGroup.strokes) ?? [],
                        this.props.measurementGroup
                      )
                    }
                  ></td>
                )}
                {this.props.measurementParameters
                  .filter(x => x.isVisible)
                  .map((parameter, i) => {
                    return (
                      <td
                        className={
                          isCombineOrTestCenterReport && parameter.propertyName === 'Score'
                            ? 'score-values'
                            : ''
                        }
                        key={parameter.propertyName}
                        onClick={() =>
                          this.selectAverage(
                            (this.props.measurementGroup && this.props.measurementGroup.strokes) ??
                              [],
                            this.props.measurementGroup
                          )
                        }
                        style={{}}
                      >
                        {ParameterHelper.getComputedParameterAverage(
                          (this.props.measurementGroup && this.props.measurementGroup.strokes) ??
                            [],
                          parameter.propertyName,
                          this.props.unitPresentation
                        )}
                      </td>
                    );
                  })}
                {this.props.measurementParameters.map((parameter, i) => {
                  if (
                    i <
                    10 -
                      this.props.measurementParameters.filter(parameter => {
                        return parameter.isVisible;
                      }).length
                  ) {
                    return (
                      <td
                        key={parameter.propertyName}
                        onClick={() =>
                          this.selectAverage(
                            (this.props.measurementGroup && this.props.measurementGroup.strokes) ??
                              [],
                            this.props.measurementGroup
                          )
                        }
                        style={{
                          borderColor: this.props.measurementGroup.color,
                        }}
                      ></td>
                    );
                  }
                  return null;
                })}
              </tr>
            )}
            {this.props.measurementGroup.isSelected && (
              <tr
                className="consistency-values"
                style={{
                  borderColor: this.props.measurementGroup.isCollapsed
                    ? this.props.measurementGroup.color
                    : this.props.measurementGroup.color,
                  backgroundColor:
                    this.props.selectedAverage || this.props.selectedStroke
                      ? '#fff'
                      : this.calculatePercentageColorOpacity(
                          this.props.measurementGroup.color ?? '',
                          10
                        ),
                }}
              >
                <td colSpan={4}>Consistency</td>
                {this.props.measurementParameters
                  .filter(x => x.isVisible)
                  .map((parameter, i) => {
                    return (
                      <td
                        className={
                          isCombineOrTestCenterReport && parameter.propertyName === 'Score'
                            ? 'score-values'
                            : ''
                        }
                        key={parameter.propertyName}
                        style={{
                          borderColor: this.props.measurementGroup.isCollapsed
                            ? this.props.measurementGroup.color
                            : '#c1c1c1',
                        }}
                      >
                        {ParameterHelper.getComputedParameterStandardDeviation(
                          (this.props.measurementGroup && this.props.measurementGroup.strokes) ??
                            [],
                          parameter.propertyName,
                          this.props.unitPresentation
                        )}
                      </td>
                    );
                  })}
                {this.props.measurementParameters.map((parameter, i) => {
                  if (
                    i <
                    10 -
                      this.props.measurementParameters.filter(parameter => {
                        return parameter.isVisible;
                      }).length
                  ) {
                    return (
                      <td
                        key={parameter.propertyName}
                        onClick={() =>
                          this.selectAverage(
                            (this.props.measurementGroup && this.props.measurementGroup.strokes) ??
                              [],
                            this.props.measurementGroup
                          )
                        }
                        style={{
                          borderColor: this.props.selectedAverage
                            ? '#c1c1c1'
                            : this.props.measurementGroup.color,
                        }}
                      ></td>
                    );
                  }
                  return null;
                })}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  private getSwitchButtonParams() {
    const result: ISwitchButtonProps = {
      measurementGroup: this.props.measurementGroup,
      groupIndex: this.props.groupIndex,
      displayStrokesGroup: this.props.displayStrokesGroup,
    };
    return result;
  }

  private getTagsListParams() {
    const result: ITagsListParams = {
      measurementGroup: this.props.measurementGroup,
      unitPresentation: this.props.unitPresentation,
    };
    return result;
  }
  private getUnitButtonParams() {
    const result: IUnitButtonParams = {
      unitSystem: this.props.unitPresentation.unitSystem,
      changeUnit: this.props.changeUnit,
    };
    return result;
  }
}
